import { Sentry } from '@nrx/error-tracking-web';
import { Button, Heading, Text } from '@nrx/ui';
import { lazy, Suspense, useEffect, useRef, type JSX } from 'react';
import { Navigate, Outlet, useLocation, useRouteError, type RouteObject } from 'react-router';

import { AppShell } from './components/app-shell.js';
import type { FeatureFlags } from './config/feature-flags.js';
import { ELIGIBILITY_ROUTE } from './features/marketing/eligibility-cta.js';
import EligibilityPage from './pages/eligibility-page.js';
import LandingPage from './pages/landing-page.js';

export { ELIGIBILITY_ROUTE };
/** The v1 flow, kept reachable so the v2 flag can be turned off without losing it. */
export const ELIGIBILITY_V1_ROUTE = '/eligibility_v1';
/** Where v2 lived before it became `/eligibility`; old links, ads and 3-D Secure returns use it. */
export const LEGACY_V2_ROUTE = '/eligibility_v2';

// Lazy: with the flag off nothing imports it, and with it on v1's bundle is still unchanged.
const EligibilityV2Page = lazy(() => import('./pages/eligibility-v2-page.js'));

/** Replaces `/eligibility_v2` with `/eligibility`, keeping the query (UTMs, Stripe's redirect_status). */
function LegacyV2Redirect(): JSX.Element {
  const { search, hash } = useLocation();
  return <Navigate replace to={{ pathname: ELIGIBILITY_ROUTE, search, hash }} />;
}

/**
 * A lazy chunk 404ing reads differently across bundlers/browsers: Vite/Rollup says "Failed to
 * fetch dynamically imported module", webpack says "Loading chunk N failed", Safari says
 * "Importing a module script failed". Tagged distinctly from any other route-boundary error
 * (which is an app defect) because this one is a deploy artifact, not our code. Checks `cause`
 * too: a module loader can wrap the original rejection rather than propagate it unchanged.
 */
const CHUNK_LOAD_RE = /dynamically imported module|loading chunk|module script failed/i;

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (CHUNK_LOAD_RE.test(error.message)) return true;
  return error.cause instanceof Error && CHUNK_LOAD_RE.test(error.cause.message);
}

/** Shown in place of the page if its lazy chunk fails to load (a stale deploy's asset 404s). */
function EligibilityV2RouteError(): JSX.Element {
  const error = useRouteError();
  // Keyed on the error itself, not `[]`: StrictMode's mount→cleanup→mount re-run sees the same
  // error object and is a no-op, but a genuinely different error (Retry fails a second, different
  // way) still gets its own report.
  const reported = useRef<unknown>(undefined);
  useEffect(() => {
    if (reported.current === error) return;
    reported.current = error;
    Sentry.captureException(error, {
      tags: {
        surface: 'route-error-boundary',
        route: 'eligibility_v2',
        ...(isChunkLoadError(error) ? { chunk_load: 'true' } : {}),
      },
    });
  }, [error]);
  return (
    <div className="mx-auto flex w-full max-w-[34rem] flex-col gap-4 px-6 py-10">
      <Heading as="h1" size="7">
        Something went wrong
      </Heading>
      <Text as="p" color="muted">
        We couldn&apos;t load this page. Please refresh and try again.
      </Text>
      <Button onClick={() => window.location.reload()}>Refresh</Button>
    </div>
  );
}

/**
 * The route table. `/eligibility` is the v2 flow when this stage's build turns v2 on and the v1
 * flow when it does not — turning the flag off is the rollback. `/eligibility_v1` is always v1.
 */
export function appRoutes(flags: FeatureFlags): RouteObject[] {
  return [
    {
      element: (
        <AppShell>
          <Outlet />
        </AppShell>
      ),
      children: [
        { path: '/', element: <LandingPage /> },
        flags.eligibilityV2
          ? {
              path: ELIGIBILITY_ROUTE,
              element: (
                <Suspense fallback={null}>
                  <EligibilityV2Page discoveryEnabled={flags.coverageDiscovery} />
                </Suspense>
              ),
              errorElement: <EligibilityV2RouteError />,
            }
          : { path: ELIGIBILITY_ROUTE, element: <EligibilityPage /> },
        { path: ELIGIBILITY_V1_ROUTE, element: <EligibilityPage /> },
        { path: LEGACY_V2_ROUTE, element: <LegacyV2Redirect /> },
      ],
    },
  ];
}
